import type { RefObject } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { AutoScroller, Cursor, Feedback, PreventSelection } from '@dnd-kit/dom';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import HighlightAltOutlined from '@mui/icons-material/HighlightAltOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { Box, Card, Skeleton } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import {
  DrawerStep,
  useDrawerSteps
} from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { convertTilesToActiveSymbols } from '@/features/dashboard/lib/tileSymbolConverter';
import type { TileBigNumber } from '@/features/dashboard/types';
import { AppType } from '@/features/dashboard/types';
import type { TileType } from '@/features/markets';
import { Tile } from '@/features/markets';
import presets from '@/shared/ui/overlays/Modal/presets';
import TooltipIconButton from '@/shared/ui/primitives/IconButton';
import { useAppSelector } from '@/store/hooks';

import {
  selectCurrentTabId,
  selectCurrentTiles
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { ConfirmationDialog } from '@/shared/ui/overlays/Modal/ConfirmationDialog';
import { useDashboardEditActions } from '../../hooks/useDashboardEditActions';
import MoveTileToDashboardMenu from './MoveTileToDashboardMenu';

import type { Sx } from '@/shared/types/uiTypes';

type TilesOrderPreview =
  | {
      components?: Array<{
        component?: {
          type?: string;
          tiles?: TileType[];
        };
      }>;
    }
  | null
  | undefined;

type TilesOrderPreviewComponent = NonNullable<
  NonNullable<TilesOrderPreview>['components']
>[number]['component'];

const getTilesFromPreviewComponent = (component: TilesOrderPreviewComponent) => {
  return component?.type === 'tiles' && Array.isArray(component.tiles) ? component.tiles : null;
};

const skeletonCardSx: Sx = { height: '56px', p: 0.25 };
const skeletonInnerSx: Sx = { height: '100%' };

const sortableTileSx: Sx = {
  borderWidth: 2,
  borderStyle: 'dashed',
  borderColor: 'gx.textDisabled',
  display: 'flex',
  width: { xs: 'calc(100% + 90px)', sm: 'calc(50% + 90px)', lg: 'calc(25% + 90px)' },
  overflow: 'hidden',
  position: 'relative',
  pr: '64px',
  pl: '24px'
};

const tileHandleSx: Sx = {
  cursor: 'grab',
  width: '24px !important',
  height: '100%',
  position: 'absolute',
  left: 0,
  top: 0
};

const tileContentSx: Sx = {
  flexGrow: 1,
  position: 'relative',
  overflow: 'hidden'
};

const tileOverlaySx: Sx = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  zIndex: Z_INDEX.raised,
  backgroundColor: 'rgba(18, 24, 54, 0.80)'
};

const tileControlsSx: Sx = {
  position: 'absolute',
  display: 'flex',
  right: 0,
  top: 0,
  gap: 0.25,
  zIndex: Z_INDEX.overlay,
  bottom: 0,
  backgroundColor: 'gx.tileDark'
};

const fullHeightButtonSx: Sx = { height: '100%' };
const buttonIconSx: Sx = { fontSize: '16px' };
const tilesContainerSx: Sx = { display: 'flex', flexWrap: 'wrap', gap: 2 };

const TileSkeleton = () => (
  <Card sx={skeletonCardSx}>
    <Skeleton
      sx={skeletonInnerSx}
      variant='rectangular'
    />
  </Card>
);

const SortableTile = ({
  tile,
  onRequestRemove,
  onRequestEdit,
  onRequestMove,
  index,
  container,
  preview
}: {
  index: number;
  tile: TileBigNumber;
  onRequestRemove: (id: string) => void;
  onRequestEdit: (id: string) => void;
  onRequestMove: (tile: TileBigNumber, anchorEl: HTMLElement) => void;
  container: RefObject<HTMLDivElement | null>;
  preview: TilesOrderPreview;
}) => {
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const { ref } = useSortable({
    id: tile.id,
    index,
    handle: handleRef,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const content = useMemo((): TileType | undefined => {
    const components = preview?.components ?? [];
    for (const app of components) {
      const previewTiles = getTilesFromPreviewComponent(app.component);
      if (previewTiles) {
        const found = previewTiles.find((t: TileType) => t.id === tile.id);
        if (found) return found;
      }
    }
    return undefined;
  }, [preview, tile.id]);

  return (
    <Box
      ref={ref}
      sx={sortableTileSx}
    >
      <TooltipIconButton
        ref={handleRef}
        sx={tileHandleSx}
        title='Drag to reorder app'
      >
        <DragIndicatorOutlinedIcon sx={{ fontSize: '21px !important' }} />
      </TooltipIconButton>

      <Box sx={tileContentSx}>
        {content ? (
          <>
            <Tile tile={content} />
            <Box sx={tileOverlaySx} />
          </>
        ) : (
          <TileSkeleton />
        )}
      </Box>
      <Box sx={tileControlsSx}>
        <TooltipIconButton
          sx={fullHeightButtonSx}
          title='Move to ...'
          onClick={e => {
            e.stopPropagation();
            onRequestMove(tile, e.currentTarget);
          }}
        >
          <HighlightAltOutlined sx={buttonIconSx} />
        </TooltipIconButton>

        <TooltipIconButton
          sx={fullHeightButtonSx}
          title='Configuration settings'
          onClick={e => {
            e.stopPropagation();
            onRequestEdit(tile.id);
          }}
        >
          <SettingsOutlined sx={buttonIconSx} />
        </TooltipIconButton>

        <TooltipIconButton
          sx={fullHeightButtonSx}
          title='Remove item'
          onClick={e => {
            e.stopPropagation();
            onRequestRemove(tile.id);
          }}
        >
          <ClearOutlined sx={buttonIconSx} />
        </TooltipIconButton>
      </Box>
    </Box>
  );
};

export const TilesOrder = ({ preview }: { preview: TilesOrderPreview }) => {
  const currentTabId = useAppSelector(selectCurrentTabId);
  const tiles = useAppSelector(selectCurrentTiles);
  const actions = useDashboardEditActions();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  // Move menu state
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);
  const [tileToMove, setTileToMove] = useState<TileBigNumber | null>(null);

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();
  const { setInitialValues } = useAppForm();

  const requestRemove = (id: string) => {
    setPendingRemoveId(id);
    setConfirmOpen(true);
  };

  const requestEdit = useCallback(
    (id: string) => {
      const tile = tiles.find(t => t.id === id);
      if (!tile) return;

      setIsEditMode(true);
      setEditingTarget({ tileId: id });

      // Load existing tiles as active symbols
      const toActive = convertTilesToActiveSymbols(tiles);

      setInitialValues({
        type: AppType.Tiles,
        symbols: { active: toActive, backlog: [] }
      });

      setDrawerStep(DrawerStep.SelectHighlightedPrice);
    },
    [tiles, setEditingTarget, setIsEditMode, setInitialValues, setDrawerStep]
  );

  const requestMove = useCallback((tile: TileBigNumber, anchorEl: HTMLElement) => {
    setTileToMove(tile);
    setMoveMenuAnchor(anchorEl);
    setMoveMenuOpen(true);
  }, []);

  const closeMoveMenu = useCallback(() => {
    setMoveMenuOpen(false);
    setMoveMenuAnchor(null);
    setTileToMove(null);
  }, []);

  const handleRemoveTileFromCurrent = useCallback(() => {
    if (tileToMove && currentTabId) {
      actions.removeTile(currentTabId, tileToMove.id);
    }
    closeMoveMenu();
  }, [tileToMove, currentTabId, actions, closeMoveMenu]);

  const handleConfirmRemove = useCallback(() => {
    if (pendingRemoveId && currentTabId) {
      actions.removeTile(currentTabId, pendingRemoveId);
      setPendingRemoveId(null);
    }
    setConfirmOpen(false);
  }, [pendingRemoveId, currentTabId, actions]);

  const container = useRef<HTMLDivElement | null>(null);
  return (
    <DragDropProvider
      plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
      onDragEnd={event => {
        const reordered = move(tiles, event);
        actions.updateCurrentTabTiles(reordered);
      }}
    >
      <Box
        ref={container}
        sx={tilesContainerSx}
      >
        {tiles.map((tile, index) => (
          <SortableTile
            key={tile.id}
            container={container}
            index={index}
            preview={preview}
            tile={tile}
            onRequestEdit={requestEdit}
            onRequestMove={requestMove}
            onRequestRemove={requestRemove}
          />
        ))}

        <ConfirmationDialog
          open={confirmOpen}
          {...presets.removeWidget}
          onCancel={() => {
            setConfirmOpen(false);
          }}
          onConfirm={handleConfirmRemove}
        />

        {tileToMove ? (
          <MoveTileToDashboardMenu
            anchorEl={moveMenuAnchor}
            open={moveMenuOpen}
            tile={tileToMove}
            onClose={closeMoveMenu}
            onRemoveFromCurrent={handleRemoveTileFromCurrent}
          />
        ) : null}
      </Box>
    </DragDropProvider>
  );
};
